import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  motion,
  useMotionValueEvent,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";

import SectionHeader from "@/components/SectionHeader";
import { useMediaQuery } from "@/lib/media";
import { scrollToSectionIndex, useSectionProgress } from "@/lib/scroll";

export type Service = {
  key: string;
  title: string;
  description: string;
  href: string;
  visual?: ReactNode;
};

export type ServicesIntro = {
  eyebrow: string;
  title: string;
  support: string;
};

const RUNWAY_PER_SCENE = 58;

const STACKED_QUERY = "(max-width: 1023px)";

const ENTER_SHIFT = 48;

const MIN_VISUAL_FIT = 0.55;

const HOLD_START = 0.36;
const HOLD_END = 0.64;

const CONTENT_HOLD = 0.16;
const CONTENT_GONE = 0.46;

function holdAndSwap(value: number) {
  const step = Math.floor(value);
  const fraction = value - step;
  const t = Math.min(
    1,
    Math.max(0, (fraction - HOLD_START) / (HOLD_END - HOLD_START)),
  );
  return step + t * t * (3 - 2 * t);
}

function IntroContent({
  intro,
  cta,
  dense,
  tight,
}: {
  intro: ServicesIntro;
  cta?: ReactNode;
  dense?: boolean;
  tight?: boolean;
}) {
  return (
    <SectionHeader
      eyebrow={intro.eyebrow}
      title={intro.title}
      support={intro.support}
      action={cta}
      dense={dense}
      tight={tight}
    />
  );
}

const step = (index: number) => ({ animationDelay: `${0.06 + index * 0.07}s` });

function SceneVisual({
  className,
  style,
  stacked,
  children,
}: {
  className: string;
  style?: CSSProperties;
  stacked: boolean;
  children: ReactNode;
}) {
  const slotRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(1);

  useLayoutEffect(() => {
    if (!stacked) {
      setFit(1);
      return;
    }

    const slot = slotRef.current;
    const content = contentRef.current;
    if (!slot || !content) return;

    const measure = () => {
      const natural = content.offsetHeight;
      const room = slot.clientHeight;
      if (!natural || !room) return;
      const next = Math.min(1, room / natural);
      setFit((prev) => (Math.abs(prev - next) < 0.01 ? prev : next));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(slot);
    observer.observe(content);
    return () => observer.disconnect();
  }, [stacked]);

  const hidden = stacked && fit < MIN_VISUAL_FIT;

  return (
    <div
      ref={slotRef}
      className={`flex justify-center ${
        stacked ? "flex-1 min-h-0 items-start" : "items-center"
      } ${className}`}
      style={style}
      aria-hidden={hidden || undefined}
    >
      <div
        ref={contentRef}
        className="w-full"
        style={
          stacked
            ? {
                transform: fit < 1 ? `scale(${fit})` : undefined,
                transformOrigin: "top center",
                visibility: hidden ? "hidden" : undefined,
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}

function ServiceContent({
  service,
  dense,
  band,
  stacked,
}: {
  service: Service;
  dense?: boolean;
  band: "roomy" | "dense" | "tight" | "minimal";
  stacked: boolean;
}) {
  const tight = band === "tight" || band === "minimal";
  return (
    <>
      <div
        className={`flex flex-col max-w-xl ${
          tight ? "gap-2" : dense ? "gap-3" : "gap-5 lg:gap-6"
        }`}
      >
        <h3
          className={`scene-item font-medium leading-tight text-ink ${
            tight
              ? "text-lg md:text-xl"
              : dense
                ? "text-xl md:text-2xl xl:text-[1.75rem]"
                : "text-2xl md:text-3xl xl:text-4xl"
          }`}
          style={step(0)}
        >
          {service.title}
        </h3>

        <p
          className={`scene-item font-light text-ink-muted max-w-xl ${
            tight
              ? "text-xs"
              : dense
                ? "text-sm xl:text-base"
                : "text-sm md:text-base"
          }`}
          style={step(1)}
        >
          {service.description}
        </p>
      </div>

      {service.visual && band !== "minimal" && (
        <SceneVisual

          stacked={stacked}
          className={`scene-item w-full mx-auto ${
            tight
              ? "max-w-[260px]"
              : dense
                ? "max-w-[340px] xl:max-w-[420px]"
                : "max-w-[420px] 2xl:max-w-[560px]"
          }`}
          style={step(2)}
        >
          {service.visual}
        </SceneVisual>
      )}
    </>
  );
}

type SceneProps = {
  index: number;
  position: MotionValue<number>;
  children: ReactNode;
  dense?: boolean;
  stacked: boolean;
};

function Scene({ index, position, children, dense, stacked }: SceneProps) {
  const offset = useTransform(position, (p) => index - p);

  const contentOpacity = useTransform(offset, (o) => {
    const distance = Math.abs(o);
    if (distance <= CONTENT_HOLD) return 1;
    return Math.max(
      0,
      1 - (distance - CONTENT_HOLD) / (CONTENT_GONE - CONTENT_HOLD),
    );
  });

  const y = useTransform(offset, (o) => o * ENTER_SHIFT);
  const scale = useTransform(offset, (o) => 1 - Math.abs(o) * 0.03);

  const visibility = useTransform(offset, (o) =>
    Math.abs(o) < 1 ? "visible" : "hidden",
  );
  const pointerEvents = useTransform(offset, (o) =>
    Math.abs(o) < 0.5 ? "auto" : "none",
  );
  const zIndex = useTransform(offset, (o) => Math.round(50 - Math.abs(o) * 10));

  return (

    <motion.div
      className={`absolute inset-0 flex ${
        stacked
          ? "items-stretch pt-[calc(2rem+5svh)] pb-12 md:pb-14"
          : "items-center"
      }`}
      style={{ visibility, pointerEvents, zIndex }}
    >
      <motion.div

        className={`page-shell relative pl-4 pr-12 md:px-8 xl:px-16 ${
          stacked ? "flex flex-col" : "grid grid-cols-2 items-center"
        } ${dense ? "gap-8 xl:gap-12" : "gap-10 xl:gap-16"}`}
        style={{ opacity: contentOpacity, y, scale }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

export default function ServicesScenes({
  intro,
  services,
  cta,
}: {
  intro: ServicesIntro;
  services: Service[];
  cta?: ReactNode;
}) {
  const sectionRef = useRef<HTMLElement>(null);

  const sceneCount = services.length + 1;
  const lastIndex = sceneCount - 1;
  const [current, setCurrent] = useState(1);

  const progress = useSectionProgress(sectionRef);
  const target = useTransform(progress, (p) =>
    1 + holdAndSwap(p * (lastIndex - 1)),
  );

  const position = useSpring(target, {
    stiffness: 90,
    damping: 26,
    mass: 0.4,
  });

  useMotionValueEvent(position, "change", (p) => {
    setCurrent(Math.min(lastIndex, Math.max(0, Math.round(p))));
  });

  const stacked = useMediaQuery(STACKED_QUERY);

  const h699 = useMediaQuery("(max-height: 699px)");
  const h559 = useMediaQuery("(max-height: 559px)");
  const h799 = useMediaQuery("(max-height: 799px)");
  const h899 = useMediaQuery("(max-height: 899px)");

  const band = h559
    ? "minimal"
    : h699
      ? "tight"
      : h799 || (stacked && h899)
        ? "dense"
        : "roomy";
  const dense = band !== "roomy";
  const tight = band === "tight" || band === "minimal";

  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      style={{ height: `${100 + sceneCount * RUNWAY_PER_SCENE}vh` }}
      aria-label={intro.eyebrow}
    >
      <div className="sticky top-0 h-svh w-full overflow-hidden flex flex-col">
        <div
          className={`relative z-[55] pointer-events-none ${
            dense ? "pt-20" : "pt-20 md:pt-24"
          }`}
        >
          <div className="page-shell px-4 md:px-8 xl:px-16">
            <IntroContent
              intro={tight ? { ...intro, support: "" } : intro}
              cta={cta}
              dense={dense}
              tight={tight}
            />
          </div>
        </div>

        <div className="relative flex-1 min-h-0">
          {services.map((service, index) => (
            <Scene
              key={service.key}
              index={index + 1}
              position={position}
              dense={dense}
              stacked={stacked}
            >
              <div className="contents" key={String(current === index + 1)}>
                <ServiceContent
                  service={service}
                  dense={dense}
                  band={band}
                  stacked={stacked}
                />
              </div>
            </Scene>
          ))}
        </div>

        <nav
          className="absolute z-[60] right-1 md:right-3 xl:right-6 top-1/2 -translate-y-1/2 flex flex-col gap-0.5"
          aria-label={intro.eyebrow}
        >
          {services.map((service, index) => (
            <button
              key={service.key}
              type="button"
              aria-label={service.title}
              aria-current={current === index + 1 ? "true" : undefined}
              onClick={() =>
                scrollToSectionIndex(sectionRef.current, index + 1, lastIndex)
              }
              className="group flex items-center justify-end p-2 cursor-pointer"
            >
              <span
                className={`block w-1.5 rounded-full transition-all duration-300 ${
                  current === index + 1
                    ? "h-5 bg-brand"
                    : "h-1.5 bg-line group-hover:bg-ink-subtle"
                }`}
              />
            </button>
          ))}
        </nav>
      </div>
    </section>
  );
}
